import { fetchWithAuth } from "../auth/googleAuth";
import { pickDriveFolder } from "../auth/googlePicker";
import { DriveFile } from "../types/google-api";
import { isFieldworkerMode, isSharedProject } from "../utils/fieldworkerMode";

// Thrown when a project-bootstrap create is attempted in Fieldworker mode, where the
// joined project is shared (owned by the manager) and must never be duplicated locally.
const FIELDWORKER_NO_CREATE =
  "Fieldworker mode is active: this shared project resource is missing and cannot be created here. Ask the project owner to set it up.";

export const ROOT_FOLDER_NAME = "MewMaps";
const ROOT_APP_PROP_KEY = "mew";
const ROOT_APP_PROP_VAL = "true";
const ROOT_ID_KEY = "mew_drive_root_id";
const FOLDER_MIME = "application/vnd.google-apps.folder";

const folderCache = new Map<string, string>();
const folderInflight = new Map<string, Promise<string>>();
let appFolderInflight: Promise<string> | null = null;

export async function ensureAppFolder(): Promise<string> {
  if (appFolderInflight) return appFolderInflight;
  appFolderInflight = (async () => {
    const cached = localStorage.getItem(ROOT_ID_KEY);
    if (cached) {
      const verify = await fetchWithAuth(
        `https://www.googleapis.com/drive/v3/files/${cached}?fields=id,trashed`
      );
      if (verify.ok) {
        const d = await verify.json() as { trashed?: boolean };
        if (!d.trashed) return cached;
      }
      localStorage.removeItem(ROOT_ID_KEY);
      folderCache.clear();
    }
    return ensureAppFolderUncached();
  })();
  try {
    return await appFolderInflight;
  } finally {
    appFolderInflight = null;
  }
}

// Read-only locate of the user's existing MewMaps root folder; returns null if none exists.
// Caches the id when found. Unlike ensureAppFolder it NEVER creates — it backs the
// onboarding "Continue" path so an owner who lost their cached ids (new browser/profile,
// cleared storage) can re-adopt their project without going through the create flow. Under
// drive.file this search only ever surfaces a folder this user owns, so a hit is always the
// owner's own project.
export async function findExistingAppFolder(): Promise<string | null> {
  const q = [
    `mimeType='${FOLDER_MIME}'`,
    "trashed=false",
    `appProperties has { key='${ROOT_APP_PROP_KEY}' and value='${ROOT_APP_PROP_VAL}' }`,
  ].join(" and ");
  const search = await fetchWithAuth(
    `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(q)}&spaces=drive&pageSize=1&fields=files(id,name)`
  );
  if (!search.ok) throw new Error(`Drive search failed (${search.status})`);
  const data = await search.json() as { files?: Array<{ id: string }> };
  if (data.files && data.files.length > 0) {
    localStorage.setItem(ROOT_ID_KEY, data.files[0].id);
    return data.files[0].id;
  }
  return null;
}

async function ensureAppFolderUncached(): Promise<string> {
  const existing = await findExistingAppFolder();
  if (existing) return existing;

  if (isFieldworkerMode()) throw new Error(FIELDWORKER_NO_CREATE);

  const create = await fetchWithAuth("https://www.googleapis.com/drive/v3/files", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: ROOT_FOLDER_NAME,
      mimeType: FOLDER_MIME,
      parents: ["root"],
      appProperties: { [ROOT_APP_PROP_KEY]: ROOT_APP_PROP_VAL, version: "1.0" },
    }),
  });
  if (!create.ok) throw new Error(`Drive folder create failed (${create.status})`);
  const created = await create.json() as { id: string };
  localStorage.setItem(ROOT_ID_KEY, created.id);
  return created.id;
}

export async function resolveOrCreateFolder(parentId: string, name: string): Promise<string> {
  const cacheKey = `${parentId}/${name}`;
  if (folderCache.has(cacheKey)) return folderCache.get(cacheKey)!;
  if (folderInflight.has(cacheKey)) return folderInflight.get(cacheKey)!;

  const p = (async () => {
    const q = `name='${name.replace(/'/g, "\\'")}' and mimeType='${FOLDER_MIME}' and '${parentId}' in parents and trashed=false`;
    const search = await fetchWithAuth(
      `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(q)}&spaces=drive&pageSize=1&fields=files(id,name)`
    );
    if (!search.ok) throw new Error(`Folder search failed (${search.status})`);
    const data = await search.json() as { files?: Array<{ id: string }> };
    if (data.files && data.files.length > 0) {
      folderCache.set(cacheKey, data.files[0].id);
      return data.files[0].id;
    }

    const create = await fetchWithAuth("https://www.googleapis.com/drive/v3/files", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, mimeType: FOLDER_MIME, parents: [parentId] }),
    });
    if (!create.ok) throw new Error(`Folder create failed (${create.status})`);
    const created = await create.json() as { id: string };
    folderCache.set(cacheKey, created.id);
    return created.id;
  })();
  folderInflight.set(cacheKey, p);
  try {
    return await p;
  } finally {
    folderInflight.delete(cacheKey);
  }
}

export async function ensureSubFolder(name: string): Promise<string> {
  const root = await ensureAppFolder();
  return resolveOrCreateFolder(root, name);
}

// ── Sharing ──────────────────────────────────────────────────────────────────
// Managing permissions on files the app created/owns works under drive.file — no broader
// (sensitive) scope is needed. Only the owner can share/revoke the MewMaps root folder
// (they created it, so they hold the grant); a shared user can't list/manage the owner's
// folder permissions under drive.file.

export interface DrivePermission {
  id: string;
  emailAddress?: string;
  role?: string;
  type?: string;
}

// Shares a file/folder with a user as Editor. Drive upserts the grant (re-sharing an
// existing user just updates it). Set notify=true to send Google's standard share email,
// with the optional emailMessage appended to it.
export async function shareFileWithUser(
  fileId: string,
  email: string,
  opts: { notify?: boolean; emailMessage?: string } = {}
): Promise<void> {
  const params = new URLSearchParams({
    sendNotificationEmail: opts.notify ? "true" : "false",
    fields: "id",
  });
  if (opts.notify && opts.emailMessage) params.set("emailMessage", opts.emailMessage);
  const res = await fetchWithAuth(
    `https://www.googleapis.com/drive/v3/files/${fileId}/permissions?${params.toString()}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role: "writer", type: "user", emailAddress: email }),
    }
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`Share failed (${res.status})${detail ? `: ${detail}` : ""}`);
  }
}

export async function listFilePermissions(fileId: string): Promise<DrivePermission[]> {
  const res = await fetchWithAuth(
    `https://www.googleapis.com/drive/v3/files/${fileId}/permissions?fields=permissions(id,emailAddress,role,type)`
  );
  if (!res.ok) throw new Error(`List permissions failed (${res.status})`);
  const data = (await res.json()) as { permissions?: DrivePermission[] };
  return data.permissions || [];
}

// Revokes a user's access by deleting their (non-owner) user permission. Returns false if
// no matching grant exists. Best-effort: a 404 on the delete is treated as already-gone.
export async function revokeFileAccess(fileId: string, email: string): Promise<boolean> {
  const target = email.trim().toLowerCase();
  const perms = await listFilePermissions(fileId);
  const match = perms.find(
    (p) => p.type === "user" && (p.emailAddress || "").toLowerCase() === target && p.role !== "owner"
  );
  if (!match) return false;
  const res = await fetchWithAuth(
    `https://www.googleapis.com/drive/v3/files/${fileId}/permissions/${match.id}`,
    { method: "DELETE" }
  );
  if (!res.ok && res.status !== 404) throw new Error(`Revoke failed (${res.status})`);
  return true;
}

// Resolve a named subfolder, trusting a previously stored id first. This is essential for
// joined (shared) projects: under drive.file a shared folder's existing children are not
// listable, so resolving "pictures" by name would never find the owner's folder and would
// create a duplicate. A Picker grant is keyed to the file id, so verifying the cached id
// with files.get works even when name-search does not.
// Only one Google Picker dialog can sensibly be shown at a time, but a single view may
// resolve several shared subfolders at once (e.g. SheetView's parallel maps + assets +
// icon preload). Serialise the prompts so the user picks them one after another.
let pickChain: Promise<unknown> = Promise.resolve();
function serializePick<T>(fn: () => Promise<T>): Promise<T> {
  const next = pickChain.then(fn, fn);
  pickChain = next.then(() => undefined, () => undefined);
  return next;
}

const subFolderInflight = new Map<string, Promise<string>>();

const normFolderName = (s?: string) => (s || "").trim().toLowerCase();

// Validates that a folder id (picked or restored from cache) really is the expected named
// subfolder ("pictures"/"maps"/"assets"/"forms") sitting directly inside this project's
// "MewMaps" root. Returns an error message, or null when valid. Used to stop a shared user
// from mistakenly selecting e.g. the "assets" folder when "pictures" was requested, and to
// retroactively reject a wrong folder cached by an older build that didn't check.
async function validateSubFolder(id: string, expectedName: string): Promise<string | null> {
  const res = await fetchWithAuth(
    `https://www.googleapis.com/drive/v3/files/${id}?fields=id,name,mimeType,trashed,parents`
  );
  if (!res.ok) return `Could not read the selected folder (${res.status}).`;
  const d = (await res.json()) as { name?: string; mimeType?: string; trashed?: boolean; parents?: string[] };
  if (d.mimeType !== FOLDER_MIME) return "That selection is not a folder.";
  if (d.trashed) return "That folder is in the trash.";
  if (normFolderName(d.name) !== normFolderName(expectedName)) {
    return `Please select the folder named "${expectedName}" (you chose "${d.name}").`;
  }
  // Containment: must share the project root as its first parent. Skipped only when the root
  // id is unknown (can't compare, so don't block).
  const root = localStorage.getItem(ROOT_ID_KEY);
  if (root && d.parents?.[0] !== root) {
    return `That "${expectedName}" folder is not inside this project's "${ROOT_FOLDER_NAME}" folder.`;
  }
  return null;
}

async function ensureCachedSubFolder(cacheKey: string, name: string): Promise<string> {
  const existing = subFolderInflight.get(cacheKey);
  if (existing) return existing;

  const p = (async () => {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      if (isSharedProject()) {
        // Shared project: re-validate name + containment so a wrong folder cached by an older
        // build (or a renamed/moved one) is dropped here, forcing a fresh Pick below.
        if (!(await validateSubFolder(cached, name))) return cached;
        localStorage.removeItem(cacheKey);
      } else {
        // Owner: they created the folder in their own root, so only confirm it still exists.
        const verify = await fetchWithAuth(
          `https://www.googleapis.com/drive/v3/files/${cached}?fields=id,trashed`
        );
        if (verify.ok && !((await verify.json()) as { trashed?: boolean }).trashed) return cached;
        localStorage.removeItem(cacheKey);
      }
    }

    // Joined (shared) project: under drive.file the manager's root children aren't listable
    // and we can't create inside their root, so name-search/create would spawn a duplicate
    // in the owner's project. Have the user Pick this shared subfolder once (the manager
    // pre-shared it when adding the member); the Pick grants per-file access and the id is
    // cached for later sessions. Owners fall through to the normal resolve/create path.
    if (isSharedProject()) {
      const picked = await serializePick(() =>
        pickDriveFolder(`Select the shared "${name}" folder (inside "${ROOT_FOLDER_NAME}")`)
      );
      if (!picked) {
        throw new Error(`The shared "${name}" folder is required. Please select it to continue.`);
      }
      const err = await validateSubFolder(picked.id, name);
      if (err) throw new Error(err);
      localStorage.setItem(cacheKey, picked.id);
      return picked.id;
    }

    const id = await ensureSubFolder(name);
    localStorage.setItem(cacheKey, id);
    return id;
  })();

  subFolderInflight.set(cacheKey, p);
  try {
    return await p;
  } finally {
    subFolderInflight.delete(cacheKey);
  }
}

export async function ensurePicturesFolder(): Promise<string> {
  return ensureCachedSubFolder("mew_pictures_folder_id", "pictures");
}

export async function ensureMapsFolder(): Promise<string> {
  return ensureCachedSubFolder("mew_maps_folder_id", "maps");
}

// The forms subfolder, cached by id so a joined (shared) manager resolves it via Pick
// instead of name-search/create (which fails for a shared root under drive.file).
export async function ensureFormsFolder(): Promise<string> {
  return ensureCachedSubFolder("mew_forms_folder_id", "forms");
}

export async function makeFilePublic(fileId: string): Promise<unknown> {
  const res = await fetchWithAuth(
    `https://www.googleapis.com/drive/v3/files/${fileId}/permissions?fields=id`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role: "reader", type: "anyone" }),
    }
  );
  if (!res.ok && res.status !== 400) {
    throw new Error(`makeFilePublic failed (${res.status})`);
  }
  return res.ok ? res.json() : null;
}

function photoBaseName(dispenserId: string): string {
  return dispenserId.replace(/-/g, "");
}

function extensionOf(file: File): string {
  const m = /\.([a-z0-9]+)$/i.exec(file.name || "");
  if (m) return m[1].toLowerCase();
  if (file.type === "image/jpeg") return "jpg";
  if (file.type === "image/png") return "png";
  if (file.type === "image/webp") return "webp";
  if (file.type === "image/heic") return "heic";
  return "bin";
}

// Flat photo storage: every dispenser's photos live directly in the single `pictures`
// folder, named `<base>-NN.ext` with the dispenser id as a filename prefix. The resulting
// file ids are returned and stored on the sheet row (写真ファイルID) — that is the only
// link, so no per-dispenser subfolder is created and no listing is ever needed. Each file
// is made public by its uploader (who created it, so the call is always permitted), which
// is what makes photos viewable cross-user via public URLs without any API access.
export async function uploadDispenserPhotos(
  dispenserId: string,
  files: File[],
  existingCount = 0
): Promise<{ fileIds: string[]; uploaded: DriveFile[] }> {
  const picturesRoot = await ensurePicturesFolder();
  const base = photoBaseName(dispenserId);
  let nextIdx = existingCount + 1;

  const uploaded: DriveFile[] = [];
  for (const f of files) {
    const ext = extensionOf(f);
    const name = `${base}-${String(nextIdx).padStart(2, "0")}.${ext}`;
    nextIdx++;
    const result = await uploadFileToFolder(picturesRoot, name, f);
    try {
      await makeFilePublic(result.id);
    } catch (e) {
      console.warn(`Failed to make photo ${result.id} public:`, e);
    }
    uploaded.push(result);
  }
  return { fileIds: uploaded.map((u) => u.id), uploaded };
}

export async function deleteFile(fileId: string): Promise<boolean> {
  const res = await fetchWithAuth(
    `https://www.googleapis.com/drive/v3/files/${fileId}`,
    { method: "DELETE" }
  );
  if (!res.ok && res.status !== 404) {
    throw new Error(`Drive delete failed (${res.status})`);
  }
  return true;
}

// Best-effort deletion of a row's photos by id. Under drive.file a user's app can only
// delete files that same user created/picked, so files uploaded by another account return
// 404 (already tolerated by deleteFile) and are skipped — the row is removed regardless.
export async function deletePhotosByIds(fileIds: string[]): Promise<void> {
  for (const id of fileIds) {
    if (!id) continue;
    try {
      await deleteFile(id);
    } catch (e) {
      console.warn(`Photo delete failed for ${id}:`, e);
    }
  }
}

export function publicImageUrl(fileId: string, width = 800): string {
  return `https://lh3.googleusercontent.com/d/${fileId}=w${width}`;
}

function buildMultipartBody(metadata: object, blob: Blob): FormData {
  const fd = new FormData();
  fd.append("metadata", new Blob([JSON.stringify(metadata)], { type: "application/json" }));
  fd.append("file", blob);
  return fd;
}

export async function uploadFileToFolder(folderId: string, fileName: string, blob: Blob): Promise<DriveFile> {
  const metadata = { name: fileName, parents: [folderId] };
  const res = await fetchWithAuth(
    "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,webViewLink",
    { method: "POST", body: buildMultipartBody(metadata, blob) }
  );
  if (!res.ok) throw new Error(`File upload failed (${res.status})`);
  return res.json() as Promise<DriveFile>;
}

export async function uploadXlsxAsSheet(folderId: string, fileName: string, blob: Blob): Promise<DriveFile> {
  const metadata = {
    name: fileName,
    parents: [folderId],
    mimeType: "application/vnd.google-apps.spreadsheet",
  };
  const res = await fetchWithAuth(
    "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,mimeType",
    { method: "POST", body: buildMultipartBody(metadata, blob) }
  );
  if (!res.ok) throw new Error(`XLSX→Sheet upload failed (${res.status})`);
  return res.json() as Promise<DriveFile>;
}

export async function listFilesInFolder(folderId: string, { mimeType, pageSize = 100 }: { mimeType?: string; pageSize?: number } = {}): Promise<DriveFile[]> {
  const clauses = [`'${folderId}' in parents`, "trashed=false"];
  if (mimeType) clauses.push(`mimeType='${mimeType.replace(/'/g, "\\'")}'`);
  const q = clauses.join(" and ");
  const r = await fetchWithAuth(
    `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(q)}&spaces=drive&pageSize=${pageSize}&orderBy=createdTime&fields=files(id,name,mimeType,createdTime)`
  );
  if (!r.ok) throw new Error(`Drive list failed (${r.status})`);
  const d = await r.json() as { files?: DriveFile[] };
  return d.files || [];
}

export async function findFileInFolder(folderId: string, name: string): Promise<DriveFile | null> {
  const q = `name='${name.replace(/'/g, "\\'")}' and '${folderId}' in parents and trashed=false`;
  const r = await fetchWithAuth(
    `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(q)}&spaces=drive&pageSize=1&fields=files(id,name,mimeType)`
  );
  if (!r.ok) throw new Error(`Drive search failed (${r.status})`);
  const d = await r.json() as { files?: DriveFile[] };
  return (d.files && d.files[0]) ? d.files[0] : null;
}

export async function copyFile(fileId: string, { name, parents }: { name?: string; parents?: string[] } = {}): Promise<DriveFile> {
  const body: { name?: string; parents?: string[] } = {};
  if (name) body.name = name;
  if (parents) body.parents = parents;
  const res = await fetchWithAuth(
    `https://www.googleapis.com/drive/v3/files/${fileId}/copy?fields=id,name,parents`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }
  );
  if (!res.ok) throw new Error(`Drive copy failed (${res.status})`);
  return res.json() as Promise<DriveFile>;
}

export async function moveFileToFolder(fileId: string, folderId: string): Promise<DriveFile> {
  const meta = await fetchWithAuth(
    `https://www.googleapis.com/drive/v3/files/${fileId}?fields=parents`
  );
  if (!meta.ok) throw new Error(`Drive get parents failed (${meta.status})`);
  const { parents = [] } = await meta.json() as { parents?: string[] };
  const removeParents = parents.join(",");
  const res = await fetchWithAuth(
    `https://www.googleapis.com/drive/v3/files/${fileId}?addParents=${folderId}&removeParents=${removeParents}&fields=id,parents`,
    { method: "PATCH", headers: { "Content-Type": "application/json" }, body: "{}" }
  );
  if (!res.ok) throw new Error(`Drive move failed (${res.status})`);
  return res.json() as Promise<DriveFile>;
}

export async function ensureAssetsFolder(): Promise<string> {
  return ensureCachedSubFolder("mew_assets_folder_id", "assets");
}

const ASSET_ICON_CACHE_KEY = "mew_icon_file_id";

export async function ensureAssetIcon(
  iconName = "icon-1.png",
  localUrl = "/assets/icon-1.png"
): Promise<string> {
  const folderId = await ensureAssetsFolder();
  const cachedId = localStorage.getItem(ASSET_ICON_CACHE_KEY);
  if (cachedId) {
    const verify = await fetchWithAuth(
      `https://www.googleapis.com/drive/v3/files/${cachedId}?fields=id,trashed`
    );
    if (verify.ok) {
      const d = await verify.json() as { trashed?: boolean };
      if (!d.trashed) return cachedId;
    }
    localStorage.removeItem(ASSET_ICON_CACHE_KEY);
  }

  const existing = await findFileInFolder(folderId, iconName);
  if (existing) {
    localStorage.setItem(ASSET_ICON_CACHE_KEY, existing.id);
    return existing.id;
  }

  if (isFieldworkerMode()) throw new Error(FIELDWORKER_NO_CREATE);

  const localRes = await fetch(localUrl);
  if (!localRes.ok) throw new Error(`Failed to load local asset ${localUrl} (${localRes.status})`);
  const blob = await localRes.blob();
  const uploaded = await uploadFileToFolder(folderId, iconName, blob);
  localStorage.setItem(ASSET_ICON_CACHE_KEY, uploaded.id);
  return uploaded.id;
}

const COMMON_PIC_CACHE_KEY = "mew_common_picture_file_id";
const COMMON_PIC_BASENAME = "common-picture";

function extFromBlob(file: File): string {
  const m = /\.([a-z0-9]+)$/i.exec(file.name || "");
  if (m) return m[1].toLowerCase();
  if (file.type === "image/jpeg") return "jpg";
  if (file.type === "image/png") return "png";
  if (file.type === "image/webp") return "webp";
  return "bin";
}

export async function findCommonPicture(): Promise<DriveFile | null> {
  const folderId = await ensureAssetsFolder();
  const q = `'${folderId}' in parents and name contains '${COMMON_PIC_BASENAME}.' and trashed=false`;
  const r = await fetchWithAuth(
    `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(q)}&spaces=drive&pageSize=1&fields=files(id,name)`
  );
  if (!r.ok) throw new Error(`Drive search failed (${r.status})`);
  const d = await r.json() as { files?: DriveFile[] };
  return (d.files && d.files[0]) ? d.files[0] : null;
}

export async function ensureCommonPicture(): Promise<string | null> {
  const cached = localStorage.getItem(COMMON_PIC_CACHE_KEY);
  if (cached) {
    const verify = await fetchWithAuth(
      `https://www.googleapis.com/drive/v3/files/${cached}?fields=id,trashed`
    );
    if (verify.ok) {
      const d = await verify.json() as { trashed?: boolean };
      if (!d.trashed) return cached;
    }
    localStorage.removeItem(COMMON_PIC_CACHE_KEY);
  }
  const existing = await findCommonPicture();
  if (existing) {
    localStorage.setItem(COMMON_PIC_CACHE_KEY, existing.id);
    return existing.id;
  }
  return null;
}

export async function uploadCommonPicture(file: File): Promise<string> {
  const folderId = await ensureAssetsFolder();
  const existing = await findCommonPicture();
  if (existing) {
    await fetchWithAuth(`https://www.googleapis.com/drive/v3/files/${existing.id}`, {
      method: "DELETE",
    });
  }
  const name = `${COMMON_PIC_BASENAME}.${extFromBlob(file)}`;
  const uploaded = await uploadFileToFolder(folderId, name, file);
  try {
    await makeFilePublic(uploaded.id);
  } catch (e) {
    console.warn("makeFilePublic on common picture failed:", e);
  }
  localStorage.setItem(COMMON_PIC_CACHE_KEY, uploaded.id);
  return uploaded.id;
}

const WATERMARK_NAME = "watermark.png";
const WATERMARK_CACHE_KEY = "mew_watermark_file_id";
const DEFAULT_WATERMARK_URL = "/assets/watermark.png";

export async function findWatermark(): Promise<DriveFile | null> {
  const folderId = await ensureAssetsFolder();
  return findFileInFolder(folderId, WATERMARK_NAME);
}

export async function ensureWatermark(): Promise<string> {
  const cached = localStorage.getItem(WATERMARK_CACHE_KEY);
  if (cached) {
    const verify = await fetchWithAuth(
      `https://www.googleapis.com/drive/v3/files/${cached}?fields=id,trashed`
    );
    if (verify.ok) {
      const d = await verify.json() as { trashed?: boolean };
      if (!d.trashed) return cached;
    }
    localStorage.removeItem(WATERMARK_CACHE_KEY);
  }
  const existing = await findWatermark();
  if (existing) {
    localStorage.setItem(WATERMARK_CACHE_KEY, existing.id);
    return existing.id;
  }
  if (isFieldworkerMode()) throw new Error(FIELDWORKER_NO_CREATE);
  return resetWatermarkToDefault();
}

export async function uploadWatermark(file: Blob): Promise<string> {
  const folderId = await ensureAssetsFolder();
  const existing = await findFileInFolder(folderId, WATERMARK_NAME);
  if (existing) {
    await fetchWithAuth(`https://www.googleapis.com/drive/v3/files/${existing.id}`, {
      method: "DELETE",
    });
  }
  const uploaded = await uploadFileToFolder(folderId, WATERMARK_NAME, file);
  try {
    await makeFilePublic(uploaded.id);
  } catch (e) {
    console.warn("makeFilePublic on watermark failed:", e);
  }
  localStorage.setItem(WATERMARK_CACHE_KEY, uploaded.id);
  return uploaded.id;
}

export async function resetWatermarkToDefault(): Promise<string> {
  const blob = await fetchLocalAssetBlob(DEFAULT_WATERMARK_URL);
  return uploadWatermark(blob);
}

export async function fetchLocalAssetBlob(localUrl = "/assets/icon-1.png"): Promise<Blob> {
  const res = await fetch(localUrl);
  if (!res.ok) throw new Error(`Failed to load ${localUrl} (${res.status})`);
  return res.blob();
}

export async function renameAndMoveFile(fileId: string, newName: string, newParentId: string): Promise<DriveFile> {
  const meta = await fetchWithAuth(
    `https://www.googleapis.com/drive/v3/files/${fileId}?fields=parents`
  );
  if (!meta.ok) throw new Error(`Drive get parents failed (${meta.status})`);
  const { parents = [] } = await meta.json() as { parents?: string[] };
  const removeParents = parents.join(",");
  const res = await fetchWithAuth(
    `https://www.googleapis.com/drive/v3/files/${fileId}?addParents=${newParentId}&removeParents=${removeParents}&fields=id,name,parents`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: newName }),
    }
  );
  if (!res.ok) throw new Error(`Drive rename+move failed (${res.status})`);
  return res.json() as Promise<DriveFile>;
}

export async function renameFile(fileId: string, newName: string): Promise<DriveFile> {
  const res = await fetchWithAuth(
    `https://www.googleapis.com/drive/v3/files/${fileId}?fields=id,name`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: newName }),
    }
  );
  if (!res.ok) throw new Error(`Drive rename failed (${res.status})`);
  return res.json() as Promise<DriveFile>;
}

export function driveFolderUrl(folderId: string): string {
  return `https://drive.google.com/drive/folders/${folderId}`;
}

// URL that opens the project's MewMaps root folder in Google Drive, or null if its id
// isn't cached. Used to deep-link the owner/manager straight to the folder they need to
// share by hand when the app can't cascade a folder share under drive.file.
export function cachedRootFolderUrl(): string | null {
  const id = localStorage.getItem(ROOT_ID_KEY);
  return id ? driveFolderUrl(id) : null;
}

export function driveFileUrl(fileId: string): string {
  return `https://drive.google.com/file/d/${fileId}/view`;
}
